import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@heroui/react';

import { useSessions, type ChatTab, type Tab } from '../hooks/useSessions';
import { usePersistentToggle } from '../hooks/usePersistentToggle';
import { useApi } from '../lib/api-context';
import { FileExplorer } from './FileExplorer';
import { SidebarHandle } from './SidebarHandle';

const STORAGE_KEY = 'bluebird.sidebar.right.v1';
const SCOPE_OVERRIDE_KEY = 'bluebird.explorer.scope.v1';
const MIN_WIDTH = 240;
const MAX_WIDTH = 520;
const DEFAULT_WIDTH = 320;

interface RightSidebarProps {
  /** Currently active tab — drives the smart-default scope. */
  activeTab: Tab | null;
}

type Scope = 'persistent' | 'workspace';

/**
 * Collapsible right sidebar — workspace file explorer.
 *
 * Smart default: if the active tab is a chat session AND its
 * per-session `/workspace/sessions/<sid>/` listing is non-empty,
 * start in the session-scoped view (`scope: 'workspace'` —
 * historical name; surfaces this session's subdir). Otherwise show
 * the workspace root (`scope: 'persistent'` — historical name;
 * surfaces every file under the user's `/workspace/`, including the
 * `sessions/` subtree). The two scope names are kept on the
 * frontend type because they're load-bearing in the API URL paths
 * (`/api/files/persistent` lists the workspace root,
 * `/api/files/workspace/{sid}` lists the session subtree per
 * CLOUDFLARE-SANDBOX.md DD-CFS-25); only the user-visible labels
 * were updated.
 *
 * The user can flip via the top toggle; their pick is stored under
 * `bluebird.explorer.scope.v1` and overrides the smart default.
 */
export function RightSidebar({ activeTab }: RightSidebarProps) {
  const { open, width, toggle, setWidth } = usePersistentToggle(STORAGE_KEY, {
    width: DEFAULT_WIDTH,
  });
  const { openFileTab } = useSessions();
  const api = useApi();

  const activeChat: ChatTab | null = useMemo(() => {
    if (activeTab && activeTab.kind === 'chat') return activeTab;
    return null;
  }, [activeTab]);
  const activeChatId = activeChat?.id ?? null;

  const [scopeOverride, setScopeOverride] = useState<Scope | null>(() =>
    loadScopeOverride(),
  );
  // Smart-default detection — starts null, fills in once the workspace
  // listing comes back (or on `activeChat` change).
  const [autoScope, setAutoScope] = useState<Scope>('persistent');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    // Skip the probe when the sidebar is collapsed (the user can't
    // see the result anyway), or when the user has already pinned a
    // scope manually. Otherwise focusing a chat tab — even with the
    // sidebar collapsed — would cold-start the per-session sandbox
    // container just to compute the smart default. Re-evaluate
    // whenever the user opens the sidebar.
    if (!open || scopeOverride !== null) {
      return;
    }
    if (!activeChatId) {
      setAutoScope('persistent');
      return;
    }
    void (async () => {
      try {
        const list = await api.listFiles('workspace', '', {
          sessionId: activeChatId,
        });
        if (cancelled) return;
        setAutoScope(list.entries.length > 0 ? 'workspace' : 'persistent');
      } catch {
        if (!cancelled) setAutoScope('persistent');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, activeChatId, refreshKey, open, scopeOverride]);

  const scope: Scope = scopeOverride ?? autoScope;

  const onPickScope = useCallback(
    (next: Scope) => {
      setScopeOverride(next);
      saveScopeOverride(next);
    },
    [],
  );

  const onClearScope = useCallback(() => {
    setScopeOverride(null);
    saveScopeOverride(null);
  }, []);

  const onRefresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  // Auto-refresh tree on tab focus (the user just came back from
  // another browser tab — files may have changed in the background).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = () => {
      if (document.visibilityState === 'visible') onRefresh();
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [onRefresh]);

  if (!open) {
    return (
      <aside
        aria-label="Workspace explorer (collapsed)"
        className="flex w-10 flex-col border-s border-white/8 py-2"
      >
        <Button
          isIconOnly
          variant="ghost"
          size="sm"
          aria-label="Expand workspace explorer"
          onPress={toggle}
        >
          <span aria-hidden="true">◀</span>
        </Button>
      </aside>
    );
  }

  return (
    <>
      <SidebarHandle
        side="right"
        width={width}
        setWidth={setWidth}
        min={MIN_WIDTH}
        max={MAX_WIDTH}
        ariaLabel="Resize workspace explorer"
      />
      <aside
        aria-label="Workspace explorer"
        className="flex h-full min-h-0 flex-col border-s border-white/8 bg-white/2"
        style={{ width: `${width}px`, flex: `0 0 ${width}px` }}
      >
        <header className="flex flex-col gap-2 border-b border-white/8 px-3 py-2 text-xs">
          <div className="flex items-center gap-2">
            <span className="font-semibold uppercase tracking-wider opacity-70">
              Workspace
            </span>
            <span className="ms-auto flex items-center gap-1">
              <Button
                isIconOnly
                variant="ghost"
                size="sm"
                aria-label="Refresh files"
                onPress={onRefresh}
              >
                <span aria-hidden="true">↻</span>
              </Button>
              <Button
                isIconOnly
                variant="ghost"
                size="sm"
                aria-label="Collapse sidebar"
                onPress={toggle}
              >
                <span aria-hidden="true">▶</span>
              </Button>
            </span>
          </div>
          <div className="flex items-center gap-1">
            <ScopeToggle
              value={scope}
              onChange={onPickScope}
              workspaceAvailable={Boolean(activeChat)}
            />
            {scopeOverride !== null && (
              <button
                type="button"
                onClick={onClearScope}
                title="Reset to smart default"
                className="rounded px-1.5 py-0.5 text-xs opacity-60 hover:bg-white/5 hover:opacity-100"
              >
                auto
              </button>
            )}
          </div>
          <div className="text-xs opacity-60">
            {scope === 'persistent' ? (
              <span>
                <code translate="no">/workspace</code> · all sessions of this
                user
              </span>
            ) : activeChat ? (
              <span>
                <code translate="no">/workspace/sessions/{activeChat.id}</code>
              </span>
            ) : (
              <span className="text-amber-300">
                Open a chat tab to browse this session's files.
              </span>
            )}
          </div>
        </header>

        {scope === 'workspace' && !activeChat ? (
          <div className="px-3 py-3 text-xs opacity-70">
            No active chat session. Switch to a chat tab to see its session
            files, or toggle to the workspace root.
          </div>
        ) : (
          <FileExplorer
            scope={scope}
            sessionId={activeChat?.id}
            refreshKey={refreshKey}
            onOpenFile={openFileTab}
          />
        )}
      </aside>
    </>
  );
}

interface ScopeToggleProps {
  value: Scope;
  onChange: (next: Scope) => void;
  workspaceAvailable: boolean;
}

function ScopeToggle({ value, onChange, workspaceAvailable }: ScopeToggleProps) {
  return (
    <div
      role="radiogroup"
      aria-label="Workspace scope"
      className="inline-flex overflow-hidden rounded-md ring-1 ring-white/10"
    >
      <ToggleButton
        active={value === 'persistent'}
        onClick={() => onChange('persistent')}
        label="all"
        title="Workspace root — every file under /workspace/, including the sessions/ subtree"
      />
      <ToggleButton
        active={value === 'workspace'}
        onClick={() => onChange('workspace')}
        label="this session"
        title="Files under /workspace/sessions/<this-session-id>/"
        disabled={!workspaceAvailable}
      />
    </div>
  );
}

function ToggleButton({
  active,
  onClick,
  label,
  disabled,
  title,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={[
        'px-2 py-1 text-xs transition-colors',
        active
          ? 'bg-blue-500/20 text-blue-100'
          : disabled
            ? 'cursor-not-allowed opacity-40'
            : 'opacity-70 hover:bg-white/5',
      ].join(' ')}
    >
      {label}
    </button>
  );
}

function loadScopeOverride(): Scope | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(SCOPE_OVERRIDE_KEY);
    if (raw === 'persistent' || raw === 'workspace') return raw;
    return null;
  } catch {
    return null;
  }
}

function saveScopeOverride(value: Scope | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (value === null) {
      window.localStorage.removeItem(SCOPE_OVERRIDE_KEY);
    } else {
      window.localStorage.setItem(SCOPE_OVERRIDE_KEY, value);
    }
  } catch {
    // Best-effort.
  }
}
